import React, { createContext, ReactNode, useContext, useState } from 'react';
import dayjs from 'dayjs';

import { deceasedContact } from '~/App/helpers/http/deceasedContact';

import { IDeceasedContact } from '~/types/IDeceasedContact';

interface IBitnetSearchContext {
  data: IDeceasedContact[];
  search: (value: string) => Promise<void>;
  loading: boolean;
  isModalOpen: boolean;
  closeModal: () => void;
  openModal: () => void;
  value: IDeceasedContact | null;
  onSelect: (
    newValue: IDeceasedContact | null,
    callBack?: (selected: IDeceasedContact | null) => void
  ) => void;
  resetMonths: () => void;
  loadMore: () => void;
  months: number;
  clearValue: () => void;
}

const BitnetSearchContext = createContext<IBitnetSearchContext | undefined>(
  undefined
);

export const useBitnetSearchContext = () => {
  const context = useContext(BitnetSearchContext);

  if (!context) {
    console.warn('useBitnetSearch need to be used inside BitnetSearchProvider');
  }

  return context as IBitnetSearchContext;
};

interface BitnetSearchProviderProps {
  children: ReactNode | ((context: IBitnetSearchContext) => ReactNode);
  value: IDeceasedContact | null;
  onChange: (newValue: IDeceasedContact | null, fieldValue?: string) => void;
}

export const BitnetSearchProvider = (props: BitnetSearchProviderProps) => {
  const { children, onChange, value } = props;
  const [data, setData] = useState<IDeceasedContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [months, setMonths] = useState(1);

  const clearValue = () => onChange(null);

  const closeModal = () => setModalOpen(false);
  const openModal = () => {
    clearValue();
    setModalOpen(true);
  };

  const onSelect = (
    newValue: IDeceasedContact | null,
    callBack?: (selected: IDeceasedContact | null) => void
  ) => {
    onChange(newValue, 'Den avlidnes namn');

    if (callBack) {
      callBack(newValue);
    }
  };

  const loadMore = () => setMonths(3);
  const resetMonths = () => setMonths(1);

  async function search(value: string) {
    setLoading(true);

    const createdFrom = dayjs().subtract(months, 'months').format('YYYY-MM-DD');

    try {
      const data = (await deceasedContact.search(value, createdFrom)) ?? [];
      if (!data.length) {
        if (months === 1) {
          return setMonths(3);
        }
      }

      setData(data);
    } catch (err) {
      console.error('Error: ', err);
    }

    setLoading(false);
  }

  const contextValue = {
    data,
    search,
    loading,
    isModalOpen,
    openModal,
    closeModal,
    value,
    onSelect,
    loadMore,
    resetMonths,
    months,
    clearValue
  };

  return (
    <BitnetSearchContext.Provider value={contextValue}>
      {typeof children === 'function' ? children(contextValue) : children}
    </BitnetSearchContext.Provider>
  );
};
